import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';
import { formatQuantity, formatSignedQuantity } from '@/shared/lib/quantityFormat';

import { useDeleteStoreOverride, useStoreOverrides, useStoreScopes, useUpsertStoreOverride } from '../../api/hooks';
import type { OverrideMode, StoreOverride } from '../../model/types';

interface OverridesPanelProps {
  canAdmin: boolean;
  seedStore: string | null;
  onToast: (message: string, severity?: 'success' | 'error' | 'warning') => void;
}

/** "Overrides" tab — audited StoreAvailabilityOverride management (spec §8.6, admin tier). */
export function OverridesPanel({ canAdmin, seedStore, onToast }: OverridesPanelProps) {
  const { t } = useTranslation('channelStock');
  const scopes = useStoreScopes();
  const [storeCode, setStoreCode] = useState<string | null>(seedStore);
  const overrides = useStoreOverrides(storeCode);
  const upsert = useUpsertStoreOverride();
  const remove = useDeleteStoreOverride();

  const [dialogOverride, setDialogOverride] = useState<StoreOverride | 'new' | null>(null);
  const [dialogFieldError, setDialogFieldError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<StoreOverride | null>(null);

  if (scopes.isLoading) return <LoadingState />;
  if (scopes.isError) return <ErrorState onRetry={() => void scopes.refetch()} />;

  const stores = (scopes.data ?? []).map((scope) => scope.storeCode);
  const rows = overrides.data ?? [];

  const isExpired = (override: StoreOverride) =>
    override.expiredApplied || (override.expiresAt != null && new Date(override.expiresAt) <= new Date());

  const createButton = (
    <Button
      variant="contained"
      startIcon={<AddRoundedIcon />}
      disabled={!canAdmin || !storeCode}
      onClick={() => setDialogOverride('new')}
    >
      {t('overrides.create')}
    </Button>
  );

  return (
    <Stack spacing={2}>
      {!canAdmin ? <Alert severity="info">{t('overrides.adminOnlyNotice')}</Alert> : null}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
        <TextField
          select
          size="small"
          label={t('overrides.storeLabel')}
          value={storeCode ?? ''}
          onChange={(event) => setStoreCode(event.target.value || null)}
          sx={{ minWidth: 260 }}
        >
          {stores.map((code) => (
            <MenuItem key={code} value={code}>{code}</MenuItem>
          ))}
        </TextField>
        <Stack direction="row" spacing={1}>
          <Tooltip title={t('refresh')}>
            <span>
              <IconButton onClick={() => void overrides.refetch()} disabled={!storeCode} aria-label="refresh-overrides">
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
          {canAdmin ? createButton : (
            <Tooltip title={t('overrides.adminOnlyNotice')}>
              <span>{createButton}</span>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      {overrides.isFetching && !overrides.isLoading ? <LinearProgress sx={{ height: 4 }} /> : <span style={{ height: 4, display: 'block' }} />}

      {!storeCode ? (
        <Typography color="text.secondary">{t('overrides.selectStoreHint')}</Typography>
      ) : overrides.isLoading ? (
        <LoadingState />
      ) : overrides.isError ? (
        <ErrorState onRetry={() => void overrides.refetch()} />
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">{t('overrides.empty')}</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('overrides.columns.product')}</TableCell>
                <TableCell>{t('overrides.columns.mode')}</TableCell>
                <TableCell align="right">{t('overrides.columns.value')}</TableCell>
                <TableCell>{t('overrides.columns.reason')}</TableCell>
                <TableCell>{t('overrides.columns.author')}</TableCell>
                <TableCell>{t('overrides.columns.expires')}</TableCell>
                <TableCell>{t('overrides.columns.status')}</TableCell>
                <TableCell align="right">{t('columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((override) => {
                const expired = isExpired(override);
                return (
                  <TableRow key={override.productId} hover>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
                      <Tooltip title={override.productId}>
                        <Typography
                          component="button"
                          variant="body2"
                          sx={{ fontFamily: 'inherit', background: 'none', border: 0, p: 0, cursor: 'pointer', color: 'inherit' }}
                          aria-label={`copy-product-${override.productId}`}
                          onClick={() => void navigator.clipboard?.writeText(override.productId)}
                        >
                          {override.productId.slice(0, 13)}…
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={override.mode === 'BLOCK' ? 'error' : override.mode === 'PIN' ? 'info' : 'default'}
                        label={t(`overrides.mode.${override.mode.toLowerCase()}`)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {override.mode === 'BLOCK'
                        ? '0'
                        : override.mode === 'DELTA'
                          ? formatSignedQuantity(override.value)
                          : formatQuantity(override.value)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Tooltip title={override.reason}>
                        <Typography noWrap variant="body2">{override.reason}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{override.author}</TableCell>
                    <TableCell>
                      {override.expiresAt ? new Date(override.expiresAt).toLocaleString() : t('overrides.noExpiry')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={expired ? 'default' : 'success'}
                        label={expired ? t('overrides.status.expired') : t('overrides.status.active')}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={canAdmin ? t('overrides.edit') : t('overrides.adminOnlyNotice')}>
                        <span>
                          <IconButton size="small" disabled={!canAdmin} aria-label={`edit-override-${override.productId}`} onClick={() => setDialogOverride(override)}>
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={canAdmin ? t('overrides.delete') : t('overrides.adminOnlyNotice')}>
                        <span>
                          <IconButton size="small" disabled={!canAdmin} aria-label={`delete-override-${override.productId}`} onClick={() => setConfirmDelete(override)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {dialogOverride !== null && storeCode ? (
        <OverrideDialog
          override={dialogOverride === 'new' ? null : dialogOverride}
          existing={rows}
          pending={upsert.isPending}
          fieldError={dialogFieldError}
          onFieldErrorClear={() => setDialogFieldError(null)}
          onClose={() => {
            setDialogOverride(null);
            setDialogFieldError(null);
          }}
          onSave={(productId, body) =>
            upsert.mutate(
              { storeCode, productId, body },
              {
                onSuccess: () => {
                  setDialogOverride(null);
                  setDialogFieldError(null);
                  onToast(t('overrides.saved'));
                },
                onError: (error) => {
                  // Review fix C3 (spec §8.6): a validation rejection — the fractional guard's 400
                  // above all — belongs on the value field, not in a generic toast.
                  if (error.status === 400 && body.mode !== 'BLOCK') {
                    setDialogFieldError(error.message);
                  } else {
                    onToast(error.message, 'error');
                  }
                },
              },
            )
          }
        />
      ) : null}

      <Dialog
        open={confirmDelete !== null}
        onClose={remove.isPending ? undefined : () => setConfirmDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('overrides.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('overrides.deleteConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            startIcon={remove.isPending ? <CircularProgress size={16} /> : undefined}
            onClick={() => {
              if (!confirmDelete || !storeCode) return;
              remove.mutate(
                { storeCode, productId: confirmDelete.productId },
                {
                  onSuccess: () => {
                    setConfirmDelete(null);
                    onToast(t('overrides.deleted'));
                  },
                  onError: (error) => onToast(error.message, 'error'),
                },
              );
            }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

interface OverrideDialogProps {
  override: StoreOverride | null;
  existing: StoreOverride[];
  pending: boolean;
  /** Server-side validation message mapped onto the value field (review fix C3). */
  fieldError: string | null;
  onFieldErrorClear: () => void;
  onClose: () => void;
  onSave: (productId: string, body: { mode: OverrideMode; value: number | null; reason: string; expiresAt: string | null }) => void;
}

function OverrideDialog({ override, existing, pending, fieldError, onFieldErrorClear, onClose, onSave }: OverrideDialogProps) {
  const { t } = useTranslation('channelStock');
  const [productId, setProductId] = useState(override?.productId ?? '');
  const [mode, setMode] = useState<OverrideMode>(override?.mode ?? 'PIN');
  const [value, setValue] = useState(override?.value != null ? String(override.value) : '');
  const [reason, setReason] = useState(override?.reason ?? '');
  const [expiresAt, setExpiresAt] = useState(override?.expiresAt ? override.expiresAt.slice(0, 16) : '');

  const replacing = !override && existing.find((entry) => entry.productId === productId.trim());
  const valid =
    productId.trim() !== '' && reason.trim() !== '' && (mode === 'BLOCK' || value.trim() !== '');

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={pending ? undefined : onClose}>
      <DialogTitle>{t('overrides.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('overrides.productIdLabel')}
            value={productId}
            onChange={(event) => setProductId(event.target.value)}
            required
            disabled={Boolean(override)}
            autoFocus={!override}
            slotProps={{ htmlInput: { style: { fontFamily: '"JetBrains Mono", monospace' } } }}
          />
          {replacing ? (
            <Alert severity="info">{t('overrides.replaceNote', { author: replacing.author })}</Alert>
          ) : null}
          <RadioGroup value={mode} onChange={(event) => setMode(event.target.value as OverrideMode)}>
            {(['PIN', 'DELTA', 'BLOCK'] as OverrideMode[]).map((entry) => (
              <FormControlLabel
                key={entry}
                value={entry}
                control={<Radio />}
                label={
                  <Stack spacing={0}>
                    <Typography>{t(`overrides.mode.${entry.toLowerCase()}`)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(`overrides.modeHint.${entry.toLowerCase()}`)}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </RadioGroup>
          {mode !== 'BLOCK' ? (
            <TextField
              label={t('overrides.valueLabel')}
              type="number"
              required
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
                onFieldErrorClear();
              }}
              error={Boolean(fieldError)}
              helperText={fieldError ?? t('overrides.fractionHelper')}
              slotProps={{ htmlInput: { step: 'any', min: mode === 'PIN' ? 0 : undefined } }}
            />
          ) : null}
          <TextField
            label={t('overrides.reasonLabel')}
            required
            multiline
            minRows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            helperText={t('overrides.reasonHelper')}
          />
          <TextField
            label={t('overrides.expiresLabel')}
            type="datetime-local"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
            helperText={t('overrides.expiresHelper')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Alert severity="info">
            {t('overrides.layerNote')} <Link href="/inventory">{t('overrides.layerNoteLink')}</Link>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>{t('cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} /> : undefined}
          onClick={() =>
            onSave(productId.trim(), {
              mode,
              value: mode === 'BLOCK' ? null : Number(value),
              reason: reason.trim(),
              expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
            })
          }
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
